import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Feather, ShieldCheck } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const next = params.get("next") || "/dashboard";
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: "", password: "", pseudonym: "", bio: "", age_attested: false, policy_accepted: false });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && (!form.age_attested || !form.policy_accepted)) {
      toast.error("Please confirm the age and policy attestations below.");
      return;
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        await register(form);
        toast.success(`Pen name registered: ${form.pseudonym}`);
      }
      nav(next);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const chk = (k) => (e) => setForm({ ...form, [k]: e.target.checked });

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const startGoogle = () => {
    const redirectUrl = `${window.location.origin}/`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grain">
      <Nav />
      <div className="max-w-md mx-auto px-5 sm:px-8 py-14">
        <Link to="/" className="flex items-center gap-2 mb-8 text-[#D9B472]" data-testid="auth-brand">
          <Feather className="w-4 h-4" /> <span className="font-serif-display text-lg">Anonscribe Archive</span>
        </Link>

        <div className="chip mb-4">{mode === "login" ? "Return" : "Enrol under a pen name"}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#F7F3EC] tracking-tight mb-3">
          {mode === "login" ? "Welcome back." : "Choose a name."}
        </h1>
        <p className="font-serif-body text-[#A8A095] mb-8">
          {mode === "login"
            ? "Sign in to sell, buy, and manage tips."
            : "Your legal name stays with us. Only your pseudonym is ever shown."}
        </p>

        <button
          type="button"
          onClick={startGoogle}
          className="w-full mb-4 flex items-center justify-center gap-3 bg-[#F7F3EC] hover:bg-white text-[#0D0C0A] rounded-sm py-3 font-mono-brass text-xs uppercase tracking-widest font-semibold transition-colors duration-200"
          data-testid="auth-google-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.5 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.22-4.74 3.22-8.32z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.16v2.85C3.97 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.85 14.1A6.6 6.6 0 015.5 12c0-.73.13-1.44.35-2.1V7.05H2.16A11 11 0 001 12c0 1.77.43 3.44 1.16 4.95l3.69-2.85z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.97 3.47 2.16 7.05l3.69 2.85C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-6" aria-hidden="true">
          <div className="flex-1 h-px bg-[rgba(217,180,114,0.15)]" />
          <span className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass">or with email</span>
          <div className="flex-1 h-px bg-[rgba(217,180,114,0.15)]" />
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Email (private)</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={upd("email")}
              className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#F7F3EC] font-mono-brass text-sm"
              data-testid="auth-email"
            />
          </div>

          {mode === "register" && (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Pseudonym (public)</label>
                <input
                  type="text"
                  required
                  minLength={2}
                  value={form.pseudonym}
                  onChange={upd("pseudonym")}
                  placeholder="e.g., The Anonymous Editor"
                  className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#D9B472] font-serif-display italic text-lg"
                  data-testid="auth-pseudonym"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Bio (optional)</label>
                <textarea
                  value={form.bio}
                  onChange={upd("bio")}
                  rows={2}
                  className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#F7F3EC] font-serif-body text-sm resize-none"
                  data-testid="auth-bio"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#6E675E] font-mono-brass block mb-1.5">Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={upd("password")}
              className="w-full bg-[#0D0C0A] border border-[rgba(217,180,114,0.15)] focus:border-[#D9B472] outline-none rounded-sm px-4 py-3 text-[#F7F3EC] font-mono-brass text-sm"
              data-testid="auth-password"
            />
          </div>

          {mode === "register" && (
            <div className="mt-4 border border-[rgba(217,180,114,0.2)] rounded-sm p-4 space-y-3 bg-[rgba(217,180,114,0.03)]">
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4 text-[#D9B472]" />
                <span className="text-[10px] uppercase tracking-widest text-[#D9B472] font-mono-brass">Content policy</span>
              </div>
              <label className="flex items-start gap-3 text-sm text-[#A8A095] font-serif-body leading-relaxed cursor-pointer">
                <input type="checkbox" required checked={form.age_attested} onChange={chk("age_attested")} className="mt-1 accent-[#D9B472]" data-testid="auth-age-attested" />
                <span>I am <strong className="text-[#F7F3EC]">18 years or older</strong>, and any person depicted, described, or referenced sexually in the work I list is <strong className="text-[#F7F3EC]">at least 18 years old</strong>.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-[#A8A095] font-serif-body leading-relaxed cursor-pointer">
                <input type="checkbox" required checked={form.policy_accepted} onChange={chk("policy_accepted")} className="mt-1 accent-[#D9B472]" data-testid="auth-policy-accepted" />
                <span>I accept the <Link to="/policy" target="_blank" className="text-[#D9B472] underline" data-testid="auth-policy-link">Content Policy</Link>. I understand CSAM and non-consensual sexual content are strictly prohibited, uploads are scanned by AI, and violations result in immediate account suspension.</span>
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-buy w-full rounded-sm py-3.5 font-mono-brass text-xs uppercase tracking-widest font-semibold mt-2"
            data-testid="auth-submit"
          >
            {loading ? "Working…" : mode === "login" ? "Sign in" : "Create pen name"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-[#A8A095] hover:text-[#D9B472] text-sm font-serif-body italic transition-colors duration-200"
            data-testid="auth-toggle"
          >
            {mode === "login" ? "Need a pen name? Enrol →" : "Already enrolled? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
